import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { db, collection, doc, getDoc, getDocs } from '../firebase';
import { getAccessibleMemberships, normalizeEmail } from '../utils/membership';
import { useSeo } from '../hooks/useSeo';
import '../App.css';

const Landing = ({ user }) => {
  const [teams, setTeams] = useState([]);
  const [editableTeamIds, setEditableTeamIds] = useState(new Set());
  const [playerTeamIds, setPlayerTeamIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useSeo({
    title: 'StatArena | Javni direktorij nogometnih timova',
    description:
      'Pregledaj javne nogometne timove na StatAreni, njihove rezultate, raspored i statistike igrača.',
    canonical: 'https://statarena.app/directory',
    robots: 'index, follow, max-image-preview:large',
    ogUrl: 'https://statarena.app/directory',
  });

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const [slugSnap, memberships] = await Promise.all([
          getDocs(collection(db, 'slugs')),
          user
            ? getAccessibleMemberships({ uid: user.uid, email: normalizeEmail(user.email) }).catch(() => [])
            : Promise.resolve([]),
        ]);

        const playerRoles = new Set(['player', 'pending-player', 'owner+player', 'owner+pending-player']);
        const memberTeamIds = new Set(
          memberships.filter((m) => playerRoles.has(m.role)).map((m) => m.teamId)
        );
        const editableIds = new Set();

        // Provjeri legacy players subcollection po email doc ID-u
        if (user?.email) {
          const normalizedEmail = normalizeEmail(user.email);
          const allTeamIds = slugSnap.docs.map((d) => d.data().teamId || d.id).filter(Boolean);
          const playerChecks = await Promise.all(
            allTeamIds.map((teamId) =>
              getDoc(doc(db, 'teams', teamId, 'players', normalizedEmail)).catch(() => null)
            )
          );
          playerChecks.forEach((snap, i) => {
            if (snap && snap.exists()) memberTeamIds.add(allTeamIds[i]);
          });
        }

        const list = await Promise.all(
          slugSnap.docs.map(async (d) => {
            const data = d.data();
            const teamId = data.teamId || d.id;
            let teamData = null;

            try {
              const teamSnap = await getDoc(doc(db, 'teams', teamId));
              if (teamSnap.exists()) teamData = teamSnap.data();
            } catch (teamError) {
              console.warn(`Nije moguce dohvatiti teams/${teamId}; koristim slug podatke.`, teamError);
            }

            if (user && teamData) {
              const isOwner = teamData.ownerUid === user.uid;
              const isManager = Array.isArray(teamData.managerUids) && teamData.managerUids.includes(user.uid);
              if (isOwner || isManager) editableIds.add(teamId);
            }

            const visibility = teamData?.visibility || data.visibility || 'public';
            const passwordEnabled = Boolean(teamData?.passwordEnabled || data.passwordEnabled);

            return {
              id: teamId,
              slug: d.id,
              name: teamData?.name || data.teamName || data.name || d.id,
              description: teamData?.description || '',
              visibility,
              passwordEnabled,
            };
          })
        );

        setEditableTeamIds(editableIds);
        setPlayerTeamIds(memberTeamIds);

        const visibleTeams = list.filter((team) =>
          team.visibility === 'public' || team.visibility === 'private' || team.passwordEnabled
        );
        visibleTeams.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setTeams(visibleTeams);
      } catch (e) {
        console.error('Greška pri dohvaćanju timova:', e);
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTeams();
  }, [user]);

  const initials = (name) => {
    if (!name) return '?';
    return name
      .trim()
      .split(/\s+/)
      .map((w) => w[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  const TeamCard = ({ team, editable = false }) => (
    <Link
      key={team.id}
      to={`/t/${team.slug || team.id}`}
      className="team-card"
      title={team.name}
    >
      <div
        className="team-card-avatar"
        style={editable ? { background: 'rgba(0,245,160,0.15)', color: '#00f5a0' } : undefined}
      >
        {initials(team.name)}
      </div>
      <div className="team-card-body">
        <h3 className="team-card-name">{team.name || 'Bez imena'}</h3>
        <div className="team-card-meta">
          <span className="team-card-slug">@{team.slug || team.id}</span>
          {!(team.passwordEnabled || team.visibility === 'private') && (
            <span className={`team-card-badge badge-${team.visibility}`}>
              {team.visibility === 'public' ? 'Javno' : 'Privatno'}
            </span>
          )}
          {(team.passwordEnabled || team.visibility === 'private') && (
            <span className="team-card-badge badge-locked">
              <span role="img" aria-label="Pod lozinkom">🔒</span> Pod lozinkom
            </span>
          )}
        </div>
        {team.description && (
          <p style={{ marginTop: 10, fontSize: '0.9rem', color: 'var(--text-2)' }}>
            {team.description}
          </p>
        )}
      </div>
    </Link>
  );

  const myPlayerTeams = teams.filter(t => playerTeamIds.has(t.id));
  const myEditableTeams = teams.filter(t => editableTeamIds.has(t.id));
  const publicTeams = teams.filter(t => !playerTeamIds.has(t.id) && !editableTeamIds.has(t.id));

  return (
    <div className="landing-page">
      <section className="landing-hero">
        <span className="hero-eyebrow">StatArena direktorij</span>
        <h1 className="landing-title">Svi javni timovi na jednom mjestu</h1>
        <p className="landing-subtitle">
          Pregledaj rezultate, raspored i statistike javnih timova iz cijele mreže.
        </p>
        <div className="landing-hero-cta">
          <Link to="/create-team" className="btn btn-primary btn-lg">
            + Kreiraj svoj tim
          </Link>
        </div>
      </section>

      {user && !loading && myPlayerTeams.length > 0 && (
        <section className="landing-section">
          <div className="landing-section-header">
            <h2>Moji timovi</h2>
            <span className="landing-count">{myPlayerTeams.length}</span>
          </div>
          <div className="team-grid">
            {myPlayerTeams.map(team => <TeamCard key={team.id} team={team} />)}
          </div>
        </section>
      )}

      {user && !loading && myEditableTeams.length > 0 && (
        <section className="landing-section">
          <div className="landing-section-header">
            <h2>Moji timovi za uređivanje</h2>
            <span className="landing-count">{myEditableTeams.length}</span>
          </div>
          <div className="team-grid">
            {myEditableTeams.map(team => (
              <TeamCard key={team.id} team={team} editable />
            ))}
          </div>
        </section>
      )}

      <section className="landing-section">
        <div className="landing-section-header">
          <h2>Javni timovi</h2>
          {!loading && <span className="landing-count">{publicTeams.length}</span>}
        </div>

        {loading && <div className="empty-inline">Učitavanje timova...</div>}
        {error && (
          <div className="empty-inline">
            <span role="img" aria-label="Upozorenje">⚠</span> {error}
          </div>
        )}

        {!loading && !error && publicTeams.length === 0 && (
          <div className="empty-inline">Još nema javnih timova.</div>
        )}

        {!loading && !error && publicTeams.length > 0 && (
          <div className="team-grid">
            {publicTeams.map(team => <TeamCard key={team.id} team={team} />)}
          </div>
        )}
      </section>
    </div>
  );
};

export default Landing;
